'use client';

import { useState } from 'react';
import {
  BodyDefault,
  Clipboard,
  IconButton,
  MarkdownPreview,
  MonoSmall,
  ReadOnlyCodeEditor,
  SegmentGroupHorizontal,
  Spinner,
  Tooltip,
} from '@terra/ui-design-system';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import { Copy01Icon, Tick01Icon } from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import { COPILOT_ACTIVITY_LOG_PATH } from '@terra/shared-dto-chat-types';
import { useChatFile } from '../../../hooks/api/useChatFilesystem';
import { ActivityLogTimeline } from './ActivityLogTimeline';

type CodeLanguage = 'python' | 'javascript' | 'json' | 'text';

const inferLanguage = (path: string): CodeLanguage => {
  if (path.endsWith('.json')) {
    return 'json';
  }

  if (path.endsWith('.py')) {
    return 'python';
  }

  if (
    path.endsWith('.js') ||
    path.endsWith('.jsx') ||
    path.endsWith('.ts') ||
    path.endsWith('.tsx')
  ) {
    return 'javascript';
  }

  return 'text';
};

const isActivityLog = (path: string): boolean =>
  path === COPILOT_ACTIVITY_LOG_PATH;

const isMarkdown = (path: string): boolean =>
  !isActivityLog(path) && (path.endsWith('.md') || path.endsWith('.mdx'));

export function ChatFilesystemFilePane({
  chatId,
  applicationId,
  path,
  onSelectPath,
}: {
  chatId: string;
  applicationId?: string | null;
  path: string | null;
  onSelectPath: (nextPath: string) => void;
}) {
  const t = useTranslations('chat.copilot.agentContext');
  const [rawActivityLog, setRawActivityLog] = useState(false);
  const {
    data: file,
    isLoading,
    isError,
  } = useChatFile(chatId, path, applicationId);

  if (!path) {
    return (
      <VStack
        flex="1"
        justifyContent="center"
        alignItems="center"
        gap="2"
        bg="bg.canvas"
        minH="0"
      >
        <MonoSmall color="fg.muted">{t('filesystemSelectFile')}</MonoSmall>
      </VStack>
    );
  }

  if (isLoading) {
    return (
      <HStack
        flex="1"
        justifyContent="center"
        alignItems="center"
        bg="bg.canvas"
        minH="0"
      >
        <Spinner size="sm" />
      </HStack>
    );
  }

  if (isError || !file) {
    return (
      <VStack
        flex="1"
        justifyContent="center"
        alignItems="center"
        bg="bg.canvas"
        minH="0"
      >
        <BodyDefault color="fg.error">
          {t('filesystemFailedToLoad')}
        </BodyDefault>
      </VStack>
    );
  }

  const activityMode = isActivityLog(file.path);
  const showRaw = !activityMode || rawActivityLog;

  return (
    <VStack flex="1" alignItems="stretch" gap="0" minH="0" overflow="hidden">
      <HStack
        px="3"
        py="2"
        gap="2"
        alignItems="center"
        justifyContent="space-between"
        borderBottomWidth="1px"
        borderColor="border.subtle"
        bg="bg.muted"
        flexShrink={0}
      >
        <MonoSmall
          color="fg.muted"
          truncate
          title={file.path}
          userSelect="text"
          flex="1"
          minW="0"
        >
          {file.path}
        </MonoSmall>
        <HStack gap="1" alignItems="center" flexShrink={0}>
          {activityMode ? (
            <SegmentGroupHorizontal
              options={[
                { value: 'timeline', label: t('filesystemTimeline') },
                { value: 'raw', label: t('filesystemRaw') },
              ]}
              value={rawActivityLog ? 'raw' : 'timeline'}
              onValueChange={({ value }) => {
                setRawActivityLog(value === 'raw');
              }}
            />
          ) : null}
          <Clipboard.Root value={file.path}>
            <Tooltip
              title={t('filesystemCopyPath')}
              positioning={{ strategy: 'fixed' }}
            >
              <Clipboard.Trigger asChild>
                <IconButton
                  type="button"
                  aria-label={t('filesystemCopyPath')}
                  variant="ghost"
                  size="xs"
                >
                  <Clipboard.Indicator copied={<Tick01Icon size={14} />}>
                    <Copy01Icon size={14} />
                  </Clipboard.Indicator>
                </IconButton>
              </Clipboard.Trigger>
            </Tooltip>
          </Clipboard.Root>
          <Clipboard.Root value={file.content}>
            <Tooltip
              title={t('filesystemCopyContent')}
              positioning={{ strategy: 'fixed' }}
            >
              <Clipboard.Trigger asChild>
                <IconButton
                  type="button"
                  aria-label={t('filesystemCopyContent')}
                  variant="ghost"
                  size="xs"
                >
                  <Clipboard.Indicator copied={<Tick01Icon size={14} />}>
                    <Copy01Icon size={14} />
                  </Clipboard.Indicator>
                </IconButton>
              </Clipboard.Trigger>
            </Tooltip>
          </Clipboard.Root>
        </HStack>
      </HStack>
      <Box
        flex="1"
        overflow="auto"
        minH="0"
        bg="bg.canvas"
        userSelect="text"
        css={{
          '& *': {
            userSelect: 'text',
          },
        }}
      >
        {activityMode && !rawActivityLog ? (
          <Box p="3" h="full" overflow="auto">
            <ActivityLogTimeline
              content={file.content}
              onSelectPath={onSelectPath}
            />
          </Box>
        ) : isMarkdown(file.path) && showRaw ? (
          <Box p="4" h="full" overflow="auto">
            <MarkdownPreview source={file.content} />
          </Box>
        ) : (
          <Box h="full" minH="0" overflow="auto">
            <ReadOnlyCodeEditor
              value={file.content}
              language={inferLanguage(file.path)}
              showLineNumbers
              borderless
              showCopyButton={false}
              maxHeight="none"
            />
          </Box>
        )}
      </Box>
    </VStack>
  );
}
